<script setup lang="ts">
import { computed } from 'vue'
import type { ShortlistFund } from '../../../api/client'
import { measureGroups, FLAG_LEGEND, type LiveContext, type MeasureGroup } from './shortlistFormat'

type GroupKey = MeasureGroup['key']

const props = withDefaults(defineProps<{
  fund: ShortlistFund; live?: LiveContext; groups?: GroupKey[]; nameOf?: (key: string) => string | null | undefined
}>(), {
  live: undefined,
  nameOf: undefined,
  groups: () => ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
})

const shown = computed(() => measureGroups(props.fund, props.live ?? {}, props.nameOf).filter(g => props.groups.includes(g.key)))

const LONG = 120
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

const GROUP_LEGEND: Record<GroupKey, string> = {
  A: 'Probe scores: the stale-NAV edge measured against the mapped future, with pass-through, edge left, CAGRs and data drops.',
  B: 'Flags: caveats raised by the scorer; each flag is explained in the list below.',
  C: 'Facts: stored fund facts (size, charges, dilution levy, fair-value clause, HL page and anything else recorded).',
  D: 'Data & timing audit: cross-source NAV agreement, NAV quality, rule concentration and the per-year / per-period / segment breakdowns.',
  E: 'Gate & pricing source: the pricing-source RAG betas and ceilings, plus the live gate verdict where the fund is live.',
  F: 'Live cross-links: production pricing RAG and dilution-levy checks (live funds only).',
  G: 'History: current status and reason; the full change log is shown below.',
}
const legendGroups = computed(() => (['A', 'B', 'C', 'D', 'E', 'F', 'G'] as GroupKey[]).filter(k => props.groups.includes(k)))
</script>

<template>
  <div class="pm" data-testid="prospect-measures">
    <section v-for="g in shown" :key="g.key" class="pm-group" :data-testid="`measure-group-${g.key}`">
      <h4>{{ g.key }} · {{ g.title }}</h4>
      <dl class="pm-rows">
        <div v-for="(r, i) in g.rows" :key="`${i}-${r.label}`" class="pm-row" :title="r.hint">
          <dt>{{ r.label }}</dt>
          <dd v-if="r.value.length > LONG">
            <details class="pm-long" data-testid="measure-long">
              <summary>{{ r.value.slice(0, LONG) }}…</summary>
              <pre>{{ r.value }}</pre>
            </details>
          </dd>
          <dd v-else>{{ r.value }}</dd>
        </div>
      </dl>
      <template v-if="g.tables">
        <div v-for="(t, ti) in g.tables" :key="`${ti}-${t.title}`" class="pm-table-block">
          <div class="pm-table-title">{{ t.title }}</div>
          <div class="pm-scroll">
            <table :data-testid="`measure-table-${slug(t.title)}`">
              <thead><tr><th v-for="(c, ci) in t.columns" :key="`${ci}-${c}`">{{ c }}</th></tr></thead>
              <tbody>
                <tr v-for="(row, i) in t.rows" :key="i"><td v-for="(cell, j) in row" :key="j">{{ cell }}</td></tr>
                <tr v-if="t.rows.length === 0"><td :colspan="t.columns.length" class="pm-empty">—</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </section>

    <details class="pm-legend" data-testid="measure-legend">
      <summary>Legend</summary>
      <ul>
        <li v-for="k in legendGroups" :key="k"><strong>{{ k }}</strong> {{ GROUP_LEGEND[k] }}</li>
      </ul>
      <ul>
        <li v-for="(text, flag) in FLAG_LEGEND" :key="flag"><strong>{{ flag }}</strong> {{ text }}</li>
      </ul>
    </details>
  </div>
</template>

<style scoped>
.pm { color: #c9d1d9; font-size: 0.85rem; min-width: 0; }
.pm-group { border-top: 1px solid #21262d; padding: 0.6rem 0; }
.pm-group:first-child { border-top: 0; }
h4 { margin: 0 0 0.4rem; font-size: 0.9rem; color: #e6edf3; }
.pm-rows { margin: 0; display: grid; grid-template-columns: 1fr; gap: 0.15rem; }
.pm-row { display: flex; flex-wrap: wrap; gap: 0.25rem 0.75rem; border-bottom: 1px solid #21262d; padding: 0.15rem 0; }
dt { color: #8b949e; flex: 0 0 11rem; max-width: 100%; overflow-wrap: anywhere; }
dd { margin: 0; flex: 1 1 8rem; min-width: 0; overflow-wrap: anywhere; }
.pm-long > summary { min-height: 44px; display: flex; align-items: center; cursor: pointer; }
.pm-long > summary:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.pm-long pre { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0.3rem 0; font-family: ui-monospace, monospace; font-size: 0.78rem; }
.pm-table-block { margin-top: 0.6rem; }
.pm-table-title { color: #8b949e; margin-bottom: 0.2rem; }
.pm-scroll { overflow-x: auto; max-width: 100%; }
table { border-collapse: collapse; min-width: 100%; }
th, td { border: 1px solid #21262d; padding: 0.2rem 0.45rem; text-align: right; white-space: nowrap; }
th:first-child, td:first-child { text-align: left; }
th { color: #8b949e; font-weight: 600; }
.pm-empty { text-align: center; color: #8b949e; }
.pm-legend { border-top: 1px solid #21262d; padding-top: 0.5rem; }
.pm-legend summary { min-height: 44px; display: flex; align-items: center; cursor: pointer; color: #8b949e; }
.pm-legend summary:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.pm-legend ul { margin: 0.4rem 0; padding-left: 1.1rem; }
.pm-legend li { overflow-wrap: anywhere; margin-bottom: 0.2rem; }
.pm-legend strong { color: #e6edf3; margin-right: 0.3rem; }
</style>
